import { config } from './config';

/**
 * Google sign-in via the Cognito hosted UI, Authorization Code + PKCE.
 *
 * PKCE rather than the implicit flow because this is a public client with no
 * secret: without the verifier, an authorization code intercepted from the
 * redirect would be enough to mint tokens. The verifier never leaves the device.
 */

const REDIRECT_URI = `${window.location.origin}/`;
const VERIFIER_KEY = 'edh.pkce';

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export function oauthConfigured(): boolean {
  return Boolean(config.authDomain && config.googleEnabled);
}

/** Send the browser to Google. Returns only if something is misconfigured. */
export async function beginGoogleSignIn(): Promise<void> {
  const verifier = base64url(crypto.getRandomValues(new Uint8Array(48)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);

  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
    identity_provider: 'Google',
    code_challenge: await challengeFor(verifier),
    code_challenge_method: 'S256',
  });
  window.location.assign(`https://${config.authDomain}/oauth2/authorize?${params}`);
}

export interface OAuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

async function tokenRequest(body: Record<string, string>): Promise<OAuthTokens> {
  const res = await fetch(`https://${config.authDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ client_id: config.userPoolClientId, ...body }),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, any>;
  if (!res.ok) {
    throw new Error(data.error_description ?? data.error ?? `token exchange failed (${res.status})`);
  }
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token ?? '',
    expiresAt: Date.now() + (data.expires_in - 60) * 1000,
  };
}

/**
 * Handle the `?code=` Cognito redirects back with. Returns null when this is an
 * ordinary page load rather than a callback.
 */
export async function completeSignInFromRedirect(): Promise<OAuthTokens | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const error = params.get('error');

  // Strip the query either way — a code left in the address bar gets shared,
  // bookmarked, and replayed, and a stale one just produces a confusing error.
  const clean = () =>
    window.history.replaceState({}, '', window.location.pathname + window.location.hash);

  if (error) {
    clean();
    // Cognito puts the PreSignUp trigger's message here, which is how an
    // uninvited person finds out they are uninvited.
    throw new Error(params.get('error_description')?.replace(/\+/g, ' ') ?? error);
  }
  if (!code) return null;

  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  clean();
  if (!verifier) throw new Error('sign-in could not be completed — please try again');
  sessionStorage.removeItem(VERIFIER_KEY);

  return tokenRequest({
    grant_type: 'authorization_code',
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });
}

export function refreshOAuth(refreshToken: string): Promise<OAuthTokens> {
  return tokenRequest({ grant_type: 'refresh_token', refresh_token: refreshToken });
}

export function hostedSignOutUrl(): string {
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: REDIRECT_URI,
  });
  return `https://${config.authDomain}/logout?${params}`;
}
